import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../../../services/api';

/**
 * Custom hook for managing voucher operations in the admin panel
 *
 * @returns {Object} Voucher management state and functions
 * @property {Array} vouchers - List of user vouchers
 * @property {boolean} loading - Loading state for voucher operations
 * @property {Object|null} stats - Voucher statistics (total, active, used, expired, totalSaved)
 * @property {Array} selectedVouchers - Array of selected voucher IDs
 * @property {Function} fetchUserVouchers - Fetch vouchers for a specific user
 * @property {Function} deleteVoucher - Delete a single voucher
 * @property {Function} bulkDeleteVouchers - Delete multiple selected vouchers
 * @property {Function} cleanupVouchers - Cleanup expired or all unused vouchers
 * @property {Function} toggleVoucherSelection - Toggle voucher selection for bulk operations
 * @property {Function} clearSelection - Clear all selected vouchers
 */
const useVouchers = () => {
  // State management
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [selectedVouchers, setSelectedVouchers] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showVouchersModal, setShowVouchersModal] = useState(false);
  const [vouchersModalUser, setVouchersModalUser] = useState(null);

  /**
   * Fetch vouchers for a specific user
   *
   * @param {string} userId - The ID of the user whose vouchers to fetch
   * @returns {Promise<Object>} Response object containing vouchers and stats
   */
  const fetchUserVouchers = async (userId) => {
    if (!userId) {
      toast.error('User ID is required');
      return { success: false };
    }

    setLoading(true);
    setCurrentUserId(userId);
    setSelectedVouchers([]);

    try {
      const response = await api.get(`/vouchers/admin/user/${userId}`);

      if (response.success) {
        setVouchers(response.data || []);
        setStats(response.stats || null);
        return { success: true, data: response.data, stats: response.stats };
      }

      return { success: false };
    } catch (error) {
      console.error('Error fetching user vouchers:', error);
      toast.error(error.message || 'Failed to load vouchers');
      setVouchers([]);
      setStats(null);
      return { success: false, error };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete a single voucher
   *
   * @param {string} voucherId - The ID of the voucher to delete
   * @returns {Promise<boolean>} Success status
   */
  const deleteVoucher = async (voucherId) => {
    if (!voucherId) {
      toast.error('Voucher ID is required');
      return false;
    }

    if (!window.confirm('Are you sure you want to delete this voucher?')) {
      return false;
    }

    try {
      const response = await api.delete(`/vouchers/admin/${voucherId}`);

      if (response.success) {
        toast.success('Voucher deleted successfully');

        // Refresh vouchers list if we have a current user
        if (currentUserId) {
          await fetchUserVouchers(currentUserId);
        }

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error deleting voucher:', error);
      toast.error(error.message || 'Failed to delete voucher');
      return false;
    }
  };

  /**
   * Delete multiple selected vouchers
   *
   * @returns {Promise<boolean>} Success status
   */
  const bulkDeleteVouchers = async () => {
    if (selectedVouchers.length === 0) {
      toast.error('Please select vouchers to delete');
      return false;
    }

    if (!currentUserId) {
      toast.error('No user selected');
      return false;
    }

    if (!window.confirm(`Delete ${selectedVouchers.length} selected voucher(s)?`)) {
      return false;
    }

    try {
      const response = await api.post('/vouchers/admin/bulk-delete', {
        voucherIds: selectedVouchers,
        userId: currentUserId
      });

      if (response.success) {
        toast.success(response.message || 'Vouchers deleted successfully');
        setSelectedVouchers([]);

        // Refresh vouchers list
        await fetchUserVouchers(currentUserId);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error bulk deleting vouchers:', error);
      toast.error(error.message || 'Failed to delete vouchers');
      return false;
    }
  };

  /**
   * Cleanup expired or unused vouchers
   *
   * @param {string} cleanupType - Type of cleanup: 'expired' or 'all'
   * @returns {Promise<boolean>} Success status
   */
  const cleanupVouchers = async (cleanupType) => {
    if (!currentUserId) {
      toast.error('No user selected');
      return false;
    }

    const message = cleanupType === 'expired'
      ? 'Delete all expired and unused vouchers?'
      : 'Delete ALL unused vouchers (including active ones)?';

    if (!window.confirm(message)) {
      return false;
    }

    try {
      const response = await api.post(`/vouchers/admin/cleanup/${currentUserId}`, {
        cleanupType
      });

      if (response.success) {
        toast.success(response.message || 'Vouchers cleaned up successfully');

        // Refresh vouchers list
        await fetchUserVouchers(currentUserId);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error cleaning up vouchers:', error);
      toast.error(error.message || 'Failed to cleanup vouchers');
      return false;
    }
  };

  /**
   * Toggle voucher selection for bulk operations
   *
   * @param {string} voucherId - The ID of the voucher to toggle
   */
  const toggleVoucherSelection = (voucherId) => {
    setSelectedVouchers(prev =>
      prev.includes(voucherId)
        ? prev.filter(id => id !== voucherId)
        : [...prev, voucherId]
    );
  };

  /**
   * Clear all selected vouchers
   */
  const clearSelection = () => {
    setSelectedVouchers([]);
  };

  /**
   * Select all vouchers
   */
  const selectAllVouchers = () => {
    const allVoucherIds = vouchers.map(voucher => voucher._id);
    setSelectedVouchers(allVoucherIds);
  };

  /**
   * Check if a voucher is selected
   *
   * @param {string} voucherId - The ID of the voucher to check
   * @returns {boolean} True if the voucher is selected
   */
  const isVoucherSelected = (voucherId) => {
    return selectedVouchers.includes(voucherId);
  };

  /**
   * Open vouchers management modal for a user
   *
   * @param {Object} userData - User data object
   */
  const openVouchersModal = async (userData) => {
    setVouchersModalUser(userData);
    setShowVouchersModal(true);

    // Fetch vouchers when opening the modal
    if (userData && userData._id) {
      await fetchUserVouchers(userData._id);
    }
  };

  /**
   * Close vouchers management modal
   */
  const closeVouchersModal = () => {
    setShowVouchersModal(false);
    setVouchersModalUser(null);
    setVouchers([]);
    setStats(null);
    setSelectedVouchers([]);
    setCurrentUserId(null);
  };

  return {
    // State
    vouchers,
    userVouchers: vouchers, // Alias for compatibility
    vouchersStats: stats, // Alias for compatibility
    loading,
    vouchersLoading: loading, // Alias for compatibility
    stats,
    selectedVouchers,
    currentUserId,
    showVouchersModal,
    vouchersModalUser,

    // Functions
    fetchUserVouchers,
    deleteVoucher,
    bulkDeleteVouchers,
    cleanupVouchers,
    toggleVoucherSelection,
    clearSelection,
    selectAllVouchers,
    isVoucherSelected,
    openVouchersModal,
    closeVouchersModal
  };
};

export default useVouchers;
